<template>
  <div class="ma-1 pa-3">
    <v-dialog v-model="showValidationDialog">
      <v-card class="search-modal">
        <h2>Validation Results</h2>
        <v-form class="search-form">
          <v-alert
            v-if="
              !!validationResult &&
              !!validationResult.errors &&
              validationResult.errors.length > 0
            "
            type="error"
          >
            <pre>{{ validationResult.errors.join("\r\n") }}</pre>
          </v-alert>
          <v-alert
            v-if="
              !!validationResult &&
              !!validationResult.warnings &&
              validationResult.warnings.length > 0
            "
            type="warning"
            outlined
          >
            <pre>{{ validationResult.warnings.join("\r\n") }}</pre>
            <pre>{{ puppyRefreshChanges.join("\r\n") }}</pre>
          </v-alert>
          <v-btn
            large
            color="primary"
            width="125px"
            :disabled="!validationResult.canBeSaved"
            v-on:click="saveChanges"
          >
            <v-icon v-if="!validationResult.canBeSaved">mdi-alert</v-icon>
            Save</v-btn
          >
        </v-form>
      </v-card>
    </v-dialog>
    <v-dialog v-model="showPuppyRefreshChanges">
      <v-card class="search-modal">
        <h2>Puppy data refresh changes</h2>
        <v-form class="search-form">          
          <v-alert
            v-if="
              !!puppyRefreshChanges &&
              puppyRefreshChanges.length > 0
            "
            type="warning"
            outlined
          >
            <pre>{{ puppyRefreshChanges.join("\r\n") }}</pre>
          </v-alert>
        </v-form>
      </v-card>
    </v-dialog>
    <v-row class="mt-2">
      <v-alert
        width="100%"
        class="pr-2 pl-2"
        v-if="errorMessage"
        dismissible
        type="error"
      >
        {{ errorMessage }}</v-alert
      >
      <v-textarea
        :disabled="!isEditMode"
        :visible="!isEditMode"
        outlined
        auto-grow
        clearable
        rows="1"
        v-model="dogInfo.adminNotes"
        label="Description of changes"
      />
      <v-btn class="ml-1 mr-1" color="primary" @click="toggleEditMode" :disabled="isChangingSire">
        {{ isEditMode ? "Save" : "Edit" }}
      </v-btn>
    </v-row>
    <v-row class="mt-2">
      <v-row>
        <v-col>            
          <v-text-field
            v-model="dogInfo.puppyId"
            label="Puppy id"
            disabled
            outlined
          />
        </v-col>
        <v-col>      
          <v-text-field
            v-model="dogInfo.litterId"
            label="Litter id"
            disabled
            outlined
          />
        </v-col>
        <v-col>
          <v-btn class="mt-5" icon v-on:click="showPuppiesSearchDialog = true" :disabled="!isEditMode" color="primary">
            <v-icon>mdi-magnify</v-icon>
          </v-btn>    
          <v-btn class="mt-5" icon v-on:click="clearPuppySelection" :disabled="!isEditMode || !(!!dogInfo.litterId || !!dogInfo.puppyId)" color="secondary">
            <font-awesome-icon icon="times"/>
          </v-btn>  
          <v-btn class="mt-5" icon v-on:click="refreshPuppyData" :disabled="!isEditMode || !(!!dogInfo.litterId || !!dogInfo.puppyId)" color="primary">
            <v-icon>mdi-refresh</v-icon>
          </v-btn>      
          <AdminLitterPuppySearchDialog 
            :litterId="dogInfo.litterId" 
            :isEditMode="isEditMode" 
            v-bind:showPuppiesSearchDialog.sync="showPuppiesSearchDialog" 
            :onPuppySelected="onPuppySelected" />
        </v-col>
      </v-row>
      <v-row>
        <v-col>
          <v-checkbox v-model="dogInfo.ascaRegistered" value disabled>
            <template v-slot:label>
              <span>ASCA Registered</span>
            </template>
          </v-checkbox>
        </v-col>
        <v-col>
          <v-autocomplete
            v-model="dogInfo.registrationType"
            label="Registration type"
            :items="colors.registrationTypes"
            item-text="description"
            item-value="abbreviation"
            :disabled="!isEditMode || isAscaPuppyRegistration"
            clearable
          ></v-autocomplete>
        </v-col>
      </v-row>
    </v-row>
    <v-row class="mt-2">
      <v-row class="mr-2">
        <v-col>
          <DatepickerFormatted
            :disabled="!isEditMode || !!dogInfo.puppyId"
            :date="dogInfo.birthdate"
            label="Birth Date"
            v-on:updateDates="updateDates($event, 'birthdate')"
          />
        </v-col>
        <v-col>
          <v-checkbox v-model="dogInfo.isDead" value :disabled="!isEditMode">
            <template v-slot:label>
              <span>Dead</span>
            </template>
          </v-checkbox>
          <DatepickerFormatted
            v-if="dogInfo.isDead"
            :disabled="!isEditMode"
            :date="dogInfo.deathDate"
            label="Date of death (optional)"
            v-on:updateDates="updateDates($event, 'deathDate')"
          />
        </v-col>
        <v-col>
          <div class="mb-1">Member photo</div>
          <v-img
            v-if="dogInfo.photoUrl"
            :src="dogInfo.photoUrl"
            :aspect-ratio="1"
            max-width="160"
            class="rounded grey lighten-4 mb-1"
          />
          <span v-else class="caption grey--text d-block mb-1">None uploaded</span>
          <input
            ref="adminPhotoInput"
            type="file"
            accept="image/jpeg,image/png,image/webp"
            class="d-none"
            @change="handleAdminPhotoSelected"
          />
          <div>
            <v-btn small text color="primary" :loading="savingPhoto" @click="chooseDogPhoto">
              {{ dogInfo.photoUrl ? "Change photo" : "Add photo" }}
            </v-btn>
            <v-btn
              v-if="dogInfo.photoUrl"
              small
              text
              color="red"
              :loading="removingPhoto"
              @click="removeDogPhoto"
            >
              Remove
            </v-btn>
          </div>
          <div v-if="photoError" class="red--text caption">{{ photoError }}</div>
        </v-col>
        <v-col>
          <v-select
            v-model="dogInfo.sex"
            label="Gender"
            :items="colors.genders"
            item-text="description"
            item-value="abbreviation"
            :disabled="!isEditMode || isAscaPuppyRegistration"
          ></v-select>
        </v-col>
      </v-row>
      <v-col>
        <DatepickerFormatted
          :disabled="!isEditMode"
          :date="dogInfo.registrationDate"
          label="Registration Date"
          v-on:updateDates="updateDates($event, 'registrationDate')"
        />
      </v-col>
    </v-row>
    <v-row class="mt-2">
      <v-col>
        <AdminDogSearchDialog
          label="Dam ID"
          v-bind:dogId.sync="dogInfo.damId"
          :initialDogId="persistedDogInfo.damId"
          :isEditMode="isEditMode || isAscaPuppyRegistration"
          :hideSearch="isAscaPuppyRegistration"
          :showOwners="true"
        />
        <v-btn
          color="primary"
          v-on:click="isChangingSire = !isChangingSire"
          :disabled="!isEditMode"
        >
          Change sire
        </v-btn> 
        <v-btn
          v-if="isChangingSire"
          color="primary"
          v-on:click="changeSire"
          :disabled="!isEditMode && !isChangingSire"
        >
          Confirm sire change & save
        </v-btn> 
        <AdminDogSearchDialog
          v-if="isChangingSire"
          label="New Sire ID"
          v-bind:dogId.sync="newSireId"
          :initialDogId="newSireId"
          :isEditMode="isEditMode && isChangingSire"
          :showOwners="true"
        />
      </v-col>
      <v-col>
        <AdminDogSearchDialog
          label="Sire ID"
          v-bind:dogId.sync="dogInfo.sireId"
          :initialDogId="persistedDogInfo.sireId"
          :isEditMode="(isEditMode || isAscaPuppyRegistration) && !isChangingSire"
          :hideSearch="isAscaPuppyRegistration"
          :showOwners="true"
        />
      </v-col>
      <v-col>
        <AdminDogSearchDialog
          label="Previous Sire ID"
          v-bind:dogId.sync="dogInfo.prevSireId"
          :initialDogId="persistedDogInfo.prevSireId"
          :isEditMode="false"
          :showOwners="true"
        />
      </v-col>
    </v-row>
    <v-row>
      <v-col class="mr-2">
        <v-row>
          <v-col>
            <v-btn
              color="primary"
              v-on:click="generateDogId"
              :disabled="!isEditMode || canGenerateNewDogId == false"
            >
              Generate new ID
            </v-btn>
          </v-col>
          <v-col>
            <v-text-field
              v-model="dogInfo.dogId"
              label="Dog ID"
              clearable
              :disabled="!isEditMode"
            />
          </v-col>
          <v-col>
            <v-checkbox
              v-model="dogInfo.notForBreeding"
              value
              :disabled="!isEditMode"
            >
              <template v-slot:label>
                <span>Not for breeding</span>
              </template>
            </v-checkbox>
            <v-checkbox
              class="ma-0 pa-0"
              v-model="dogInfo.isRevoked"
              :disabled="
                !isEditMode ||
                !(
                  dogInfo &&
                  dogInfo.registrationType &&
                  revokableRegistrationTypes.includes(
                    dogInfo.registrationType.trim()
                  )
                ) ||
                (dogInfo && !dogInfo.dogId)
              "
              value
            >
              <template v-slot:label>
                <span>Revoked</span>
              </template>
            </v-checkbox>
          </v-col>
        </v-row>
        <v-row>
          <v-col>
            <v-text-field
              v-model="dogInfo.name"
              label="Dog Name"
              :disabled="!isEditMode"
              outlined
              :rules="[(v) => (!!v && v.length <= 50) || 'Max 50 characters']"
              counter="50"
            />
          </v-col>
        </v-row>
        <v-row>
          <v-col>
            <v-text-field
              v-model="dogInfo.callName"
              label="Call Name"
              :disabled="!isEditMode"
              outlined
              :rules="[
                (v) => !v || (!!v && v.length <= 50) || 'Max 50 characters',
              ]"
              counter="50"
            />
          </v-col>
          <v-col>
            <v-text-field
              v-model="dogInfo.tattoo"
              label="Tattoo/Chip No"
              :disabled="!isEditMode"
              outlined
            />
          </v-col>
        </v-row>  
        <v-row>
          <v-textarea
            class="mt-5"
            disabled
            @input="updateText('notes', $event)"
            :value="dogInfo.notes"
            outlined
            rows="6"
            auto-grow
            label="Notes"
          ></v-textarea>
        </v-row>
      </v-col>
      <v-col>
        <v-row>
          <v-col>
            <v-autocomplete
              v-model="dogInfo.breed"
              label="Breed"
              :items="colors.breeds"
              item-text="description"
              item-value="abbreviation"
              :disabled="
                !isEditMode ||
                isAscaPuppyRegistration ||
                (dogInfo &&
                  dogInfo.registrationType &&
                  ascaDogsRegistrations.includes(
                    dogInfo.registrationType.trim()
                  ))
              "
            ></v-autocomplete>
          </v-col>
        </v-row>
        <v-row>
          <v-col>
            <v-select
              v-model="dogInfo.body"
              label="Body color"
              :items="colors.mainColors"
              item-text="description"
              item-value="abbreviation"
              :disabled="!isEditMode || isAscaPuppyRegistration"
            ></v-select>
          </v-col>
          <v-col>
            <v-select
              v-model="dogInfo.trim"
              label="Trim color"
              :items="colors.trimColors"
              item-text="description"
              item-value="abbreviation"
              :disabled="!isEditMode"
            ></v-select>
          </v-col>
        </v-row>
        <v-row>
          <v-col>
            <v-select
              v-model="dogInfo.leftEye"
              label="Left eye color"
              :items="colors.eyeColors"
              item-text="description"
              item-value="abbreviation"
              :disabled="!isEditMode"
            ></v-select>
          </v-col>
          <v-col>
            <v-select
              v-model="dogInfo.rightEye"
              label="Right eye color"
              :items="colors.eyeColors"
              item-text="description"
              item-value="abbreviation"
              :disabled="!isEditMode"
            ></v-select>
          </v-col>
        </v-row>
        <v-row class="ml-2" style="min-height: 130px">
          <AdminEditableCoownersList
            :isEditMode="isEditMode"
            :owners="dogInfo.owners"
            :ownershipType="dogOwnershipType"
            v-on:updateSelectedPeople="updateSelectedPeople($event)"
          />
        </v-row>
      </v-col>
    </v-row>
    <v-snackbar v-model="showInfoMessage" title color="green">
      {{ infoMessage }}
        <template v-slot:action="{ attrs }">
          <v-btn color="primary" text v-bind="attrs" @click="showInfoMessage = false">
            Close
          </v-btn>
        </template>
      </v-snackbar>
    <v-snackbar v-model="showErrorMessage" title color="red">
        {{ errorMessage }}
        <template v-slot:action="{ attrs }">
          <v-btn color="primary" text v-bind="attrs" @click="showErrorMessage = false">
            Close
          </v-btn>
        </template>
    </v-snackbar>
  </div>
</template>

<script lang="ts">
import Vue from "vue";
import DatepickerFormatted from "../../../common/inputs/DatepickerFormatted.vue";
import AdminEditableCoownersList, {
  OwnershipType,
} from "../../AdminEditableCoownersList.vue";
import AdminDogSearchDialog from "../../AdminDogSearchDialog.vue";
import AdminLitterPuppySearchDialog from "../../AdminLitterPuppySearchDialog.vue";

import dogService from "@/services/admin/dogService";
import { resizeImage } from "@/helpers/resize-image";
import { getErrorMessage } from "@/services/errorHandler";
import lookupsService from "@/services/lookupsService";
import { getAscaToday } from "@/helpers/date-utils";

import { Messages } from "@/constants";

export default Vue.extend({
  name: "DogRegistrationDetails",
  components: {
    AdminEditableCoownersList,
    DatepickerFormatted,
    AdminDogSearchDialog,
    AdminLitterPuppySearchDialog,
  },
  props: {
    dogId: String,
  },
  data: function () {
    return {
      removingPhoto: false,
      savingPhoto: false,
      photoError: "",
      newSireId: "",
      dogInfo: {
        dogId: "",
        owners: [] as any,
        puppyId: null,
        registrationDate: getAscaToday(),
        isRevoked: false,
        notForBreeding: false,
      } as any,
      persistedDogInfo: {
        damId: null,
        sireId: null,
        prevSireId: null,
      } as any,
      puppyInfo: {
        dogId: "",
      } as any,
      isEditMode: false,
      colors: {},
      selectedPuppyId: null,
      showValidationDialog: false,
      validationResult: { errors: [] as string[], warnings: [] as string[]},
      registrationsWithAutomaticIdGeneration: ["E", "F", "HIII", "LEP", "T"],
      ascaDogsRegistrations: ["E", "F", "HIII", "LEP"],
      revokableRegistrationTypes: ["E", "F"],
      showPuppiesSearchDialog: false,
      errorMessage: '',
      infoMessage: '',
      showErrorMessage: false,
      showInfoMessage: false,
      isChangingSire: false,
      puppyRefreshChanges: [] as string [],
      showPuppyRefreshChanges: false,
    };
  },
  computed: {
    dogOwnershipType: function () {
      return OwnershipType.Dog;
    },
    isAscaPuppyRegistration: function () {
      return !(
        this.$data.dogInfo.puppyId == null || !this.$data.dogInfo.puppyId
      );
    },
    canGenerateNewDogId: function () {
      return (
        this.$data.dogInfo &&
        this.$data.dogInfo.registrationType &&
        this.$data.registrationsWithAutomaticIdGeneration.includes(
          this.$data.dogInfo.registrationType.trim()
        )
      );
    },
  },
  created: function () {
    this.reloadData();
  },
  methods: {
    chooseDogPhoto: function () {
      this.photoError = "";
      (this.$refs.adminPhotoInput as HTMLInputElement).click();
    },
    handleAdminPhotoSelected: async function (event: Event) {
      const input = event.target as HTMLInputElement;
      const file = input.files && input.files[0];
      input.value = "";

      if (!file) return;

      this.savingPhoto = true;
      try {
        const resized = await resizeImage(file);
        const res = await dogService.uploadDogPhoto(this.dogInfo.dogId, resized);

        if (res.success) {
          this.reloadData();
        } else {
          this.photoError = res.errorMessage;
        }
      } catch (e: any) {
        this.photoError = getErrorMessage(e);
      } finally {
        this.savingPhoto = false;
      }
    },
    removeDogPhoto: async function () {
      this.removingPhoto = true;
      try {
        const res = await dogService.deleteDogPhoto(this.dogInfo.dogId);
        if (res.success) {
          this.dogInfo.photoUrl = null;
        }
      } finally {
        this.removingPhoto = false;
      }
    },
    reloadData: function () {
      if (this.dogId) {
        dogService
          .getDogRegistrationInfoById(this.dogId)
          .then((res: any) => {
            this.dogInfo = Object.assign({}, res);
            this.persistedDogInfo = {
              damId: res.damId,
              sireId: res.sireId,
              prevSireId: res.prevSireId,
            };
            this.selectedPuppyId = this.dogInfo.puppyId;
          });
      } else {
        this.dogInfo = {
          dogId: "",
          owners: [] as any,
          puppyId: null,
          registrationDate: getAscaToday(),
        };
        this.selectedPuppyId = null;
        this.isEditMode = true;
      }
      lookupsService.getDogsAttributes().then((res) => {
        this.colors = res;
        this.showValidationDialog = false;
        this.validationResult = { errors: [], warnings: []};
        this.errorMessage = "";
      });
      
      this.puppyRefreshChanges = [];
    },
    toggleEditMode: function () {
      if (this.isEditMode) {
        this.validateAndConfirmSavingChanges();
      } else {
        this.isEditMode = true;
      }
    },
    updateDates: function (val: any, key: string) {
      this.dogInfo[key] = val.date;
      this.dogInfo = Object.assign({}, this.dogInfo);
    },
    updateSelectedPeople: function (val: any) {
      //workaround for setting up the primary owner
      //and issues with reactivity in Vue https://v2.vuejs.org/v2/guide/reactivity.html?redirect=true#For-Arrays
      if (
        val.newPeople &&
        (val.newPeople.length === 1 ||
          (val.newPeople.length > 0 &&
            val.newPeople.findIndex((own: any) => own.isPrimary) == -1))
      ) {
        val.newPeople[0].isPrimary = true;
      }
      if (!!this.dogInfo.owners && val.newPeople && val.newPeople.length > 0) {
        this.dogInfo.owners.splice(val.newPeople.length);
      }
      this.$set(this.dogInfo, "owners", val.newPeople);
    },
    onPuppySelected: function(puppyInfo: any){
      this.showPuppiesSearchDialog = false;
      dogService
        .getPuppyRegistrationInfoById(puppyInfo.id)
        .then((res: any) => {
          this.dogInfo.body = res.body;
          this.dogInfo.sex = res.sex;
          this.dogInfo.litterId = res.litterId;
          this.dogInfo.birthdate = res.birthdate;
          this.dogInfo.damId = res.damId;
          this.dogInfo.sireId = res.sireId;
          this.dogInfo.breed = res.breed;
          this.dogInfo.puppyId = res.puppyId;
          this.puppyInfo.dogId = res.dogId;
          this.dogInfo.registrationType = "E";
          this.persistedDogInfo = {
              damId: res.damId,
              sireId: res.sireId
            };
        });
      
      this.puppyRefreshChanges = [];
    },
    clearPuppySelection: function() {
      this.showPuppiesSearchDialog = false;
      this.dogInfo.litterId = null;
      this.dogInfo.puppyId = null;
      this.dogInfo.body = null;
      this.dogInfo.sex = null;
      this.dogInfo.birthdate = null;
      this.dogInfo.damId = null;
      this.dogInfo.sireId = null;
      this.dogInfo.breed = null;
      this.puppyInfo.dogId = null;
      this.dogInfo.registrationType = null;
      this.persistedDogInfo = {
        damId: null,
        sireId: null,
        AdminDogSearchDialog: null,
      };

      this.puppyRefreshChanges = [];
    },
    refreshPuppyData: function() {
      dogService
        .getPuppyRegistrationInfoById(this.dogInfo.puppyId)
        .then((res: any) => {

          let warnings = [] as string[];  
          if(this.dogInfo.body != res.body){
            warnings.push(`Main color changed from '${this.dogInfo.body}' to '${res.body}'`);
          }
          if(this.dogInfo.sex != res.sex){
            warnings.push(`Gender changed from '${this.dogInfo.sex}' to '${res.sex}'`);
          }
          if(this.dogInfo.litterId != res.litterId){
            warnings.push(`Litter ID# from '${this.dogInfo.litterId}' to '${res.litterId}'`);
          }
          if(this.dogInfo.birthdate != res.birthdate){
            warnings.push(`Birth date changed from '${this.dogInfo.birthdate}' to '${res.birthdate}'`);
          }
          if(this.dogInfo.damId != res.damId?.trim()){
            warnings.push(`Dam ID# changed from '${this.dogInfo.damId}' to '${res.damId?.trim()}'`);
          }
          if(this.dogInfo.sireId != res.sireId?.trim()){
            warnings.push(`Sire ID# changed from '${this.dogInfo.sireId}' to '${res.sireId?.trim()}'`);
          }
          if(this.dogInfo.breed != res.breed){
            warnings.push(`Breed from '${this.dogInfo.breed}' to '${res.breed}'`);
          }
          if(this.dogInfo.puppyId != res.puppyId){
            warnings.push(`Puppy ID# from '${this.dogInfo.puppyId}' to '${res.puppyId}'`);
          }
          if(this.dogInfo.dogId != res.dogId){
            warnings.push(`Dog ID# from '${this.dogInfo.dogId}' to '${res.dogId}'`);
          }
          if(this.dogInfo.registrationType != 'E'){
            warnings.push(`Registration type from '${this.dogInfo.registrationType}' to 'E'`);
          }

          this.dogInfo.body = res.body;
          this.dogInfo.sex = res.sex;
          this.dogInfo.litterId = res.litterId;
          this.dogInfo.birthdate = res.birthdate;
          this.dogInfo.damId = res.damId?.trim();
          this.dogInfo.sireId = res.sireId?.trim();
          this.dogInfo.breed = res.breed;
          this.dogInfo.puppyId = res.puppyId;
          this.puppyInfo.dogId = res.dogId;
          this.dogInfo.registrationType = "E";
          this.persistedDogInfo = {
              damId: res.damId,
              sireId: res.sireId
            };

          if(warnings.length > 0) {
            this.puppyRefreshChanges = warnings;
            this.showPuppyRefreshChanges = true;
          }
        });
    },
    generateDogId: function () {
      this.$store.dispatch("changeLoaderStatus", true);
      dogService
        .generateDogId({ registrationType: this.dogInfo.registrationType })
        .then((res: any) => {
          this.dogInfo.dogId = res.id;
          if (res.success && res.id) {
            this.dogInfo.dogId = res.id;
            this.dogInfo.isRevoked = false;
            this.dogInfo.notForBreeding = this.dogInfo.registrationType === 'LEP' ? true : false;
            this.errorMessage = "";
          } else {
            this.errorMessage =
              "Couldn't generate new ID." + res.messages.join(", ");
            this.showErrorMessage = true;
          }
        })
        .catch((e) => {
            this.errorMessage ="Couldn't generate new ID.";
            this.showErrorMessage = true;
        })
        .finally(() => this.$store.dispatch("changeLoaderStatus", false));
    },
    validateAndConfirmSavingChanges: function () {
      var currentDogId = this.dogId ? this.dogId : "";
      this.dogInfo.currentDogId = currentDogId;
      dogService
        .validateDogRegistrationInfoEdit(this.dogInfo)
        .then((res: any) => {
          this.validationResult = res;
          if (res.hasValidationIssues) {
            this.showValidationDialog = true;
          } else {
            this.saveChanges();
          }
        });
    },
    saveChanges: function () {
      this.showValidationDialog = false;
      this.$store.dispatch("changeLoaderStatus", true);
      dogService
        .editDogById(this.dogInfo.currentDogId, this.dogInfo)
        .then((res) => {
          if (res.success) {
            if (res.id && res.id?.trim() != this.dogId?.trim()) {
              this.$router.push({ path: `/admin/dogs/${encodeURIComponent(res.id.trim())}` })
            }            
            this.reloadData();
            this.showInfoMessage = true;
            this.infoMessage = Messages.SaveSuccessful;
          } else {
            this.errorMessage = res.messages.join(", ");
          }
        })
        .catch((e) => {
          this.errorMessage = Messages.SaveError;
          this.showErrorMessage = true;          
        })
        .finally(() => this.$store.dispatch("changeLoaderStatus", false));
    },    
    changeSire: function() {
      const oldSireId = this.persistedDogInfo.sireId;
      const newSireId = this.newSireId;

      const notes = `Sire changed from ${oldSireId} to ${newSireId}.`;

      this.dogInfo.sireId = newSireId;
      this.dogInfo.prevSireId = oldSireId;
      this.dogInfo.adminNotes = !!this.dogInfo.adminNotes ? this.dogInfo.adminNotes + " | " + notes : notes;

      this.validateAndConfirmSavingChanges();

      this.isChangingSire = false;
      this.newSireId = "";
    },
  },
  watch: {
    selectedPuppyId: function (newVal: String) {
      if (!newVal || this.dogInfo.puppyId != newVal) {
        //if puppy id cleared, clear all related data
        this.dogInfo.litterId = null;
        this.dogInfo.body = null;
        this.dogInfo.sex = null;
        this.dogInfo.birthdate = null;
        this.dogInfo.puppyId = null;
        this.puppyInfo.dogId = null;
      }
    },
    "dogInfo.registrationType": function (newVal: String, oldVal: String) {
      if (newVal && this.ascaDogsRegistrations.includes(newVal?.trim())) {
        this.dogInfo.ascaRegistered = true;
        this.dogInfo.breed = "AS ";

        if (this.isEditMode && newVal?.trim() === "LEP") {
          this.dogInfo.notForBreeding = true;
        }
      } else if (oldVal) {
        //only update if there was previous value selected, otherwise leave values that were persisted
        this.dogInfo.ascaRegistered = false;
        this.dogInfo.breed = null;
      }
      if (newVal && oldVal) {
        this.dogInfo.isRevoked = false;
      }
    },
    dogId: function (newVal: String) {
      this.reloadData();
    },
  },
});
</script>
